import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, Divider, ButtonGroup, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PrintIcon from '@mui/icons-material/Print';
import config from '../../config';
import { useRoomNames } from './useRoomNames';
import { isGstApplicable } from './gstSettings';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '';

const getInvoicePrefix = (date) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    const now = new Date();
    let year = now.getFullYear();
    if (now.getMonth() > 2) year += 1;
    return `INV${year.toString().slice(-2)}_`;
  }
  let year = d.getFullYear();
  if (d.getMonth() > 2) year += 1;
  return `INV${year.toString().slice(-2)}_`;
};

const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  const belowTwenty = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];
  const thousands = ['', 'Thousand', 'Lakh', 'Crore'];

  const convertChunk = (n) => {
    if (n === 0) return '';
    if (n < 20) return belowTwenty[n];
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] + (n % 10 ? ' ' + belowTwenty[n % 10] : '')
      );
    return (
      belowTwenty[Math.floor(n / 100)] +
      ' Hundred' +
      (n % 100 ? ' ' + convertChunk(n % 100) : '')
    );
  };

  let [integerPart, decimalPart = ''] = num.toFixed(2).split('.');
  integerPart = parseInt(integerPart);
  decimalPart = parseInt(decimalPart);

  if (integerPart === 0) return 'Zero Rupees Only';

  let word = '',
    chunkCount = 0;
  while (integerPart > 0) {
    let chunk = integerPart % 1000;
    if (chunk) {
      let chunkWord = convertChunk(chunk);
      if (chunkCount > 0) chunkWord += ' ' + thousands[chunkCount];
      word = chunkWord + (word ? ' ' + word : '');
    }
    integerPart = Math.floor(integerPart / 1000);
    chunkCount++;
  }

  let result = word + ' Rupees';
  if (decimalPart > 0) result += ' and ' + convertChunk(decimalPart) + ' Paise';
  result += ' Only';
  return result;
};

export default function InvoicePage() {
  const { bookingId } = useParams();
  const { roomLabel } = useRoomNames();
  const [invoices, setInvoices] = useState([]);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [loading, setLoading] = useState(true);
  // Merge all of a booking's invoices (room + add-on + restaurant) into ONE
  // consolidated invoice. Default ON. OFF = original separate behaviour.
  const [mergeInvoices, setMergeInvoices] = useState(true);
  // Restaurant/bar food line-items charged to this booking (from Orders /
  // OrderDetails). Folded into the merged invoice.
  const [foodLines, setFoodLines] = useState([]);
  // Diagnostic for the merge: 'loading' | 'ok' | 'empty' | 'error ...'
  const [foodStatus, setFoodStatus] = useState('loading');

  // Phase 4 — print mode toggle + reprint awareness.
  // 'A4' is the default detailed layout; 'Thermal' renders an 80mm-friendly
  // condensed view via the inline CSS injected below.
  const [printMode, setPrintMode] = useState('A4');
  const [reprintCount, setReprintCount] = useState(0);

  // Fetch how many times this invoice has been printed before. The
  // frontend stamps DUPLICATE when reprintCount > 0 at the moment of
  // viewing — the first view itself counts as the "original".
  useEffect(() => {
    if (!bookingId) return;
    (async () => {
      try {
        const token = sessionStorage.getItem('token');
        const r = await fetch(
          `${config.API_BASE_URL}/api/invoice-print/count?bookingId=${encodeURIComponent(bookingId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (r.ok) {
          const d = await r.json();
          setReprintCount(Number(d?.count || 0));
        } else {
          // Fail safe: if the count can't be confirmed, assume it's a reprint so
          // the DUPLICATE watermark still shows (don't silently pass an original).
          setReprintCount(1);
        }
      } catch (e) {
        setReprintCount(1); // fail safe — show DUPLICATE when count unknown
      }
    })();
  }, [bookingId]);

  const logReprintAndPrint = async (mode) => {
    try {
      const token = sessionStorage.getItem('token');
      const userId = parseInt(sessionStorage.getItem('userId') || '0', 10) || null;
      await fetch(`${config.API_BASE_URL}/api/invoice-print/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bookingId: Number(bookingId),
          companyId: company?.id,
          hotelId:   company?.hotelId || company?.id,
          userId,
          printMode: mode,
        }),
      });
      setReprintCount((n) => n + 1);
    } catch (e) { /* never block printing on a log failure */ }
    setPrintMode(mode);
    // Give React a frame to apply the class before the print dialog opens.
    setTimeout(() => window.print(), 50);
  };

  const handleWhatsappShare = () => {
    if (!invoices.length) return;
    const inv = invoices[0];
    const guest = `${inv.userfirstname || ''} ${inv.userlastname || ''}`.trim();
    const total = Math.round(
      Math.max(0, Number(inv.totelPrice || 0) - Number(inv.couponAmount || 0)),
    );
    const url = `${window.location.origin}/InvoicePage/${bookingId}`;
    const text =
      `*${inv.hotelName || 'Invoice'}*\n` +
      `Invoice for ${guest}\n` +
      `Booking #${bookingId}\n` +
      `Amount: ${Companycurrency}${total}\n\n` +
      `View invoice: ${url}`;
    const phone = (inv.usermobilenumber || '').replace(/\D/g, '');
    const waUrl = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
  };

  const formatPrice = (v) =>
    Number(v || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatRate = (v) =>
    Number(v || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await fetch(
          `${config.API_BASE_URL}/Home/Getinvoicedata/${encodeURIComponent(bookingId)}/${encodeURIComponent(company?.id)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error('Failed to fetch invoice');
        const data = await res.json();
        const invoiceData = Array.isArray(data) ? data : [data];
        console.log(data);
        setInvoices(invoiceData);
        if (invoiceData[0]?.termsAndCondition) {
          setTermsAndConditions(invoiceData[0].termsAndCondition);
        }
      } catch (e) {
        console.error('Error fetching invoice:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [bookingId]);

  // Restaurant/bar food charged to this booking — folded into the merged bill.
  // We reuse the EXISTING, already-deployed GetRestroinvoice endpoint via the
  // booking's restaurant order id(s) (invoice.orderid — the same linkage the
  // "View res Invoice" button uses), so no new backend deploy is needed.
  useEffect(() => {
    if (!invoices.length) return;
    const orderIds = [
      ...new Set(
        invoices.map((i) => Number(i.orderid || 0)).filter((id) => id > 0),
      ),
    ];
    if (!orderIds.length) {
      setFoodStatus('empty');
      setFoodLines([]);
      return;
    }
    (async () => {
      try {
        const token = sessionStorage.getItem('token');
        const results = await Promise.all(
          orderIds.map((id) =>
            fetch(`${config.API_BASE_URL}/home/GetRestroinvoice/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null),
          ),
        );
        const ok = results.filter(Boolean);
        const items = ok.flatMap((inv) =>
          Array.isArray(inv.items) ? inv.items : [],
        );
        setFoodLines(items);
        setFoodStatus(
          items.length ? 'ok' : ok.length ? 'empty' : 'error fetch',
        );
      } catch (e) {
        setFoodStatus('error');
        setFoodLines([]);
      }
    })();
  }, [invoices]);

  const handleViewInvoice = (invoice) => {
    window.open(
      `${window.location.origin}/RestroInvoice/${invoice.orderid}`,
      '_blank',
    );
  };

  if (loading) return <Typography>Loading invoice...</Typography>;
  if (!invoices.length) return <Typography>No invoice found.</Typography>;

  const gstOn = isGstApplicable();
  const hasFood = (foodLines || []).length > 0;
  // Merge mode: when ON and there's more than one invoice OR any restaurant
  // food, render ONE paper (the room invoice) and fold every invoice's add-ons
  // + the food bill + totals into it.
  const merged = mergeInvoices && (invoices.length > 1 || hasFood);
  const displayInvoices = merged ? [invoices[0]] : invoices;

  // Restaurant/bar food → invoice lines. From GetRestroinvoice each item has
  // `price` (GST-INCLUSIVE unit price), `qty`, and `gst` (rate %). Gross =
  // price×qty; back-calc the base from it (never add tax on top). GST OFF → 0.
  const buildFoodLines = () =>
    (foodLines || []).map((it) => {
      const qty = Number(it.qty || 1) || 1;
      const gross = Number(it.price || 0) * qty;
      const pct = gstOn ? Number(it.gst || 0) : 0;
      const base = pct > 0 ? gross / (1 + pct / 100) : gross;
      return {
        name: it.name || 'Item',
        hsn: '',
        qty,
        base,
        // Price column shows the per-unit EX-TAX price (WITHOUT GST), so food is
        // consistent with the room row and with the CGST/SGST columns. `base` is
        // the ex-tax LINE total, so the ex-tax unit price = base / qty.
        unitPrice: qty > 0 ? base / qty : base,
        gstPct: pct,
        gstAmt: gross - base,
        total: gross,
        taxType: 'GST',
      };
    });

  // Build the add-on lines for a single invoice (GST-aware). Reused so the
  // merged paper can gather add-ons from ALL invoices at once.
  const buildAddons = (inv) => {
    const names = inv.addonNames || [];
    const prices = inv.addonPrices || [];
    const qtys = inv.addonQty || [];
    const flags = inv.addonflegRaw ? inv.addonflegRaw.split(',') : [];
    const gstPercs =
      gstOn && inv.addonGstPercRaw ? inv.addonGstPercRaw.split(',') : [];
    const hsns = inv.addonHsnRaw ? inv.addonHsnRaw.split(',') : [];
    const taxTypes = inv.addonTaxTypeRaw ? inv.addonTaxTypeRaw.split(',') : [];
    const fallback = gstOn ? Number(inv.gst || 0) : 0;
    return names.map((n, i) => {
      const price = Number(prices[i] || 0);
      const qty = Number(qtys[i] || 1) || 1;
      const ratePct = gstOn ? Number(gstPercs[i] || 0) || fallback : 0;
      const taxType = (taxTypes[i] || 'GST').toUpperCase().trim() || 'GST';
      const lineGross = price * qty;
      const lineBase = ratePct > 0 ? lineGross / (1 + ratePct / 100) : lineGross;
      const lineGst = lineGross - lineBase;
      return {
        name: n,
        qty,
        price: lineGross,
        base: lineBase,
        gstPct: ratePct,
        gstAmt: lineGst,
        taxType,
        hsn: hsns[i] || '',
        flag: flags[i] || '',
      };
    });
  };

  return (
    <Box
      sx={{ p: 1, background: '#f5f5f5', minHeight: '100vh' }}
      className={`invoice-root ${printMode === 'Thermal' ? 'thermal-mode' : 'a4-mode'}`}>

      {/* Phase 4 — Print-only CSS. Watermarks are absolutely positioned
          and rotated; thermal mode narrows the page to 80mm and shrinks
          margins so the same React tree prints on a POS roll printer. */}
      <style>{`
        .invoice-watermark {
          position: absolute; inset: 0; pointer-events: none;
          display: flex; align-items: center; justify-content: center;
          font-weight: 900; letter-spacing: 0.3em; opacity: 0.12;
          font-size: 8rem; transform: rotate(-25deg); user-select: none;
          z-index: 1;
        }
        .invoice-watermark.duplicate { color: #c62828; }
        .invoice-watermark.cancelled { color: #424242; }

        @media print {
          .no-print { display: none !important; }
          .invoice-watermark { opacity: 0.18 !important; }
          @page { size: A4; margin: 10mm; }
        }
        @media print {
          .thermal-mode .invoice-paper {
            width: 76mm !important;
            box-shadow: none !important;
            padding: 4mm !important;
            font-size: 10px !important;
          }
          .thermal-mode .invoice-paper * { font-size: inherit !important; }
          .thermal-mode .invoice-paper table { font-size: 9px !important; }
          .thermal-mode .invoice-paper th,
          .thermal-mode .invoice-paper td {
            padding: 2px 3px !important; border: 0 !important;
            border-bottom: 1px dashed #999 !important;
          }
          .thermal-mode .invoice-paper img { max-height: 40px !important; }
          .thermal-mode .invoice-watermark { font-size: 3rem !important; }
          .thermal-mode @page { size: 80mm auto; margin: 2mm; }
        }
      `}</style>

      <Box
        className='no-print'
        display='flex'
        justifyContent='flex-end'
        alignItems='center'
        gap={1}
        mb={1.5}
        flexWrap='wrap'
        sx={{ '@media print': { display: 'none' } }}>
        {reprintCount > 0 && (
          <Typography variant='caption' color='warning.main' sx={{ mr: 'auto' }}>
            ⚠ This invoice has been printed {reprintCount} time{reprintCount > 1 ? 's' : ''} —
            next print will carry a DUPLICATE watermark.
          </Typography>
        )}

        {/* Food-merge diagnostic — makes the otherwise invisible fetch result
            visible so it's obvious WHY food did/didn't merge. */}
        <Typography
          variant='caption'
          sx={{
            mr: 1,
            fontWeight: 600,
            color:
              foodStatus === 'ok'
                ? 'success.main'
                : foodStatus.startsWith('error')
                  ? 'error.main'
                  : 'text.secondary',
          }}>
          {foodStatus === 'ok' &&
            `🍽 ${foodLines.length} restaurant item(s) found — merged`}
          {foodStatus === 'empty' &&
            'No restaurant order linked to this booking'}
          {foodStatus.startsWith('error') &&
            `⚠ Restaurant data not reachable (${foodStatus}) — redeploy backend`}
        </Typography>

        {(invoices.length > 1 || hasFood) && (
          <FormControlLabel
            control={
              <Checkbox
                checked={mergeInvoices}
                onChange={(e) => setMergeInvoices(e.target.checked)}
                size='small'
              />
            }
            label='Merge All Invoices'
            sx={{ mr: 1 }}
          />
        )}

        <ButtonGroup variant='contained' size='small'>
          <Tooltip title='Standard A4 print'>
            <Button
              startIcon={<PrintIcon />}
              color='primary'
              onClick={() => logReprintAndPrint('A4')}>
              Print A4
            </Button>
          </Tooltip>
          <Tooltip title='80mm thermal printer layout'>
            <Button
              startIcon={<PrintIcon />}
              color='secondary'
              onClick={() => logReprintAndPrint('Thermal')}>
              Print Thermal
            </Button>
          </Tooltip>
        </ButtonGroup>

        <Button
          size='small'
          variant='contained'
          startIcon={<WhatsAppIcon />}
          sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebd5d' } }}
          onClick={handleWhatsappShare}>
          Share on WhatsApp
        </Button>

        {/* One restaurant-invoice button per booking — not per invoice. All
            invoices here belong to the same booking, so render it once. */}
        {invoices.length > 0 && (
          <Button
            size='small'
            variant='contained'
            color='success'
            onClick={() => handleViewInvoice(invoices[0])}>
            View res Invoice
          </Button>
        )}
      </Box>

      {displayInvoices.map((invoice, index) => {
        // Company GST switch OFF → strip every GST component so the bill prints
        // with NO tax at all (room, cover charge, and add-ons). Zeroing the
        // source fields here makes every downstream `invoice.cGst` / `invoice.gst`
        // read 0 without touching each calculation individually.
        if (!isGstApplicable()) {
          invoice.cGst = 0;
          invoice.sGst = 0;
          invoice.igst = 0;
          invoice.gst = 0;
          // NOTE: do NOT zero coverchargebaseamt — it's the cover BASE (not a
          // tax) and is used to strip the cover out of the room line. Zeroing it
          // left the cover inside the room base AND as a flag-C add-on line →
          // the cover was billed twice when company GST was OFF.
          invoice.covercgst = 0;
          invoice.coversgst = 0;
          invoice.coverigst = 0;
          invoice.roomChargeCgst = 0;
          invoice.roomChargesgst = 0;
          invoice.roomChargeTotalgst = 0;
          invoice.addonGstPercRaw = '';
        }
        // Add-on lines. When merged, gather them from the add-on invoice(s)
        // (index >= 1) — the same rows the non-merged view shows on the second
        // paper — so the single consolidated paper lists them all WITHOUT
        // double-counting the room invoice.
        const addons = merged
          ? invoices.slice(1).flatMap(buildAddons)
          : buildAddons(invoice);

        const checkInDate = new Date(invoice.checkIn);
        const checkOutDate = new Date(invoice.checkOut);
        // Clamp to >= 1: same-day/day-use bookings give 0 (Infinity rate) and
        // bad/timezone data can give negative or NaN, corrupting the printed
        // rate and quantity.
        const nights =
          Math.max(1, Math.ceil(
            (checkOutDate - checkInDate) / (1000 * 60 * 60 * 24),
          ) || 0) || 1;
        const roomCount = invoice.roomNumbers
          ? invoice.roomNumbers.split(',').filter((r) => r.trim()).length
          : 1;
        // The cover / early-check-in charge (a flag-C add-on) is folded into
        // baseprice by the SP, but it is billed on the SEPARATE add-on invoice.
        // We strip it back out of the room invoice ONLY when that add-on invoice
        // actually exists (2 invoices returned) — otherwise the add-on amount
        // would show on BOTH bills. When the booking has no separate add-on
        // invoice (a single invoice is returned), stripping it would drop the
        // cover charge from the bill entirely, so we keep it in the room total
        // so it still affects the booking amount. (Fields are 0 with no cover.)
        const hasAddonInvoice = invoices.length === 2;
        const coverBaseAmt = hasAddonInvoice
          ? Number(invoice.coverchargebaseamt || 0)
          : 0;
        const coverGstAmt = hasAddonInvoice
          ? Number(invoice.covercgst || 0) +
            Number(invoice.coversgst || 0) +
            Number(invoice.coverigst || 0)
          : 0;
        const roomOnlyBase = Number(invoice.baseprice || 0) - coverBaseAmt;
        const roomnightbasprice = roomOnlyBase / roomCount / nights;
        const finaldiscount = invoice.price - roomnightbasprice;
        // In merge mode the single paper is never "add-ons only" — it shows the
        // room AND all add-ons together.
        const isAddonsOnlyInvoice =
          !merged && invoices.length === 2 && index === 1;
        const showAddons = merged || isAddonsOnlyInvoice;
        const allAddons = showAddons
          ? [
              ...addons.filter((a) => a.flag === 'B'),
              ...addons.filter((a) => a.flag === 'C'),
            ]
          : [];

        const hasAddons = allAddons.length > 0;
        const basePrice = roomOnlyBase;
        const caludiscount = invoice.dicountedprice / roomCount / nights;
        const discountPerc =
          invoice.discountType === 'Percentage'
            ? Number(invoice.discountValue || 0)
            : 0;
        const discountPercAmount = caludiscount * (discountPerc / 100);

        // Grand total must reconcile with the lines actually printed in the
        // table; otherwise rounding/cover/late-checkout amounts that live in
        // the server's totelPrice make the bottom line drift away from the
        // per-item sum. Compute from the same numbers the table uses.
        const addonsGrandTotal = allAddons.reduce(
          (s, a) => s + Number(a.price || 0),
          0,
        );
        // Extra "Room Charges" (separate from the per-night room tariff).
        // roomchargegst holds the GROSS (GST-INCLUSIVE) amount — its GST is
        // already inside it, so DON'T add GST on top. Back-calc the pre-tax base
        // from the gross minus its stored GST.
        const roomChargeGross = isAddonsOnlyInvoice
          ? 0
          : Number(invoice.roomchargegst || 0);
        const roomChargeGstAmt = isAddonsOnlyInvoice
          ? 0
          : Number(invoice.roomChargeTotalgst || 0);
        const roomChargeBase = Math.max(0, roomChargeGross - roomChargeGstAmt);
        const roomGrandTotal =
          basePrice +
          Number(invoice.cGst || 0) +
          Number(invoice.sGst || 0) +
          Number(invoice.igst || 0) -
          coverGstAmt +
          roomChargeGross;
        // Restaurant/bar food total (gross, GST-inclusive) — added to the
        // merged grand total only.
        const foodGrandTotal =
          merged && hasFood
            ? (foodLines || []).reduce(
                (s, f) =>
                  s + Number(f.price || 0) * (Number(f.qty || 1) || 1),
                0,
              )
            : 0;
        // Coupon / voucher reduces the room invoice (never the add-ons-only
        // second paper). A 100% voucher must net the payable to ₹0, so subtract
        // it from the grand total and floor at 0 (no negative / pending).
        const couponAmount = isAddonsOnlyInvoice
          ? 0
          : Math.max(0, Number(invoice.couponAmount || 0));
        const grandTotal = Math.max(
          0,
          Math.round(
            (isAddonsOnlyInvoice
              ? addonsGrandTotal
              : // Merged paper: room (+ room charge) + every add-on + food.
                merged
                ? roomGrandTotal + addonsGrandTotal + foodGrandTotal
                : roomGrandTotal) - couponAmount,
          ),
        );
        const grandTotalInWords = numberToWords(grandTotal);

        const isCancelled = !!invoice.isCancel;
        const showDuplicate = reprintCount > 0 && !isCancelled;
        return (
          <Box
            key={index}
            className='invoice-paper'
            sx={{
              position: 'relative',
              width: { xs: '100%', sm: '850px' },
              mx: 'auto',
              background: '#fff',
              p: { xs: 2, sm: '22px 28px' },
              borderRadius: '8px',
              boxShadow: '0 10px 35px rgba(0,0,0,0.1)',
              mb: 3,
              overflow: 'hidden',
              '@media print': {
                p: '18px 25px',
                boxShadow: 'none',
                borderRadius: 0,
                pageBreakAfter:
                  index < displayInvoices.length - 1 ? 'always' : 'auto',
              },
            }}>
            {isCancelled && (
              <div className='invoice-watermark cancelled'>CANCELLED</div>
            )}
            {/* DUPLICATE watermark removed per requirement. */}
            {/* ==================== PREMIUM HEADER ==================== */}
            <Box
              display='flex'
              alignItems='flex-start'
              justifyContent='space-between'
              mb={3}>
              {/* Logo - Left */}
              <Box>
                {company.logo && (
                  <img
                    src={company.logo}
                    alt='Logo'
                    style={{
                      height: '92px',
                      maxWidth: '260px',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Box>

              {/* Company Name + Address + Mobile - CENTER */}
              <Box textAlign='center' flex={1} sx={{ mt: 1 }}>
                <Typography
                  variant='h4'
                  fontWeight='700'
                  color='#1a237e'
                  gutterBottom>
                  {invoice.hotelName}
                </Typography>
                <Typography fontSize='0.78rem' color='text.secondary'>
                  {invoice.address}
                </Typography>
                <Typography fontSize='0.78rem' color='text.secondary'>
                  {invoice.city}, {invoice.pincode}
                </Typography>
                <Typography fontSize='0.78rem' sx={{ mt: 0.5 }}>
                  <strong>Mobile:</strong> {invoice.mobile}
                </Typography>
              </Box>

              {/* TAX INVOICE + Invoice Details - Right */}
              <Box textAlign='right' sx={{ minWidth: '200px' }}>
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #1565c0, #1976d2)',
                    color: '#fff',
                    px: 4,
                    py: 0.8,
                    borderRadius: '6px',
                    fontWeight: 800,
                    fontSize: '1.15rem',
                    display: 'inline-block',
                    mb: 1,
                    '@media print': { background: '#1976d2 !important' },
                  }}>
                  TAX INVOICE
                  {!merged && invoices.length === 2
                    ? index === 0
                      ? ' (Room Charges)'
                      : ' (Add-ons)'
                    : ''}
                </Box>

                <Typography fontSize='0.82rem' fontWeight='600'>
                  Invoice #:{' '}
                  <strong>
                    {getInvoicePrefix(invoice.checkOut)}
                    {invoice.invoiceNumber}
                  </strong>
                </Typography>
                <Typography fontSize='0.78rem' color='text.secondary'>
                  Date: {new Date().toLocaleDateString('en-IN')}
                </Typography>
              </Box>
            </Box>

            {/* ==================== GST NO (LEFT) + FOOD LICENSE (RIGHT) ==================== */}
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={2.5}>
              <Typography fontSize='0.78rem'>
                <strong>GST NO:</strong> {invoice.gstNumber}
              </Typography>

              {invoice.foodLicenseNumber && (
                <Typography fontSize='0.78rem'>
                  <strong>Food License:</strong> {invoice.foodLicenseNumber}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2, borderColor: '#333' }} />

            {/* Guest & Booking Details */}
            <Box
              display='flex'
              justifyContent='space-between'
              mb={3}
              flexWrap='wrap'
              gap={2}>
              <Box
                sx={{
                  flex: '1 1 48%',
                  minWidth: '280px',
                  p: 1.8,
                  background: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                }}>
                <Typography
                  fontWeight='700'
                  fontSize='0.85rem'
                  mb={0.8}
                  color='#1565c0'>
                  {invoice.companyname?.trim()
                    ? 'Bill To (Company)'
                    : 'Bill To (Guest)'}
                </Typography>
                {invoice.companyname?.trim() ? (
                  <>
                    <Typography fontWeight='600'>
                      {invoice.companyname}
                    </Typography>
                    <Typography fontSize='0.73rem'>
                      {invoice.companyAddress}, {invoice.companyCity},{' '}
                      {invoice.companyState}
                    </Typography>
                    {invoice.companyEmail && (
                      <Typography fontSize='0.73rem'>
                        {invoice.companyEmail}
                      </Typography>
                    )}
                    {invoice.companyMobile && (
                      <Typography fontSize='0.73rem'>
                        Mob: {invoice.companyMobile}
                      </Typography>
                    )}
                    {invoice.gstno && (
                      <Typography fontSize='0.73rem'>
                        GST No: {invoice.gstno}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography fontWeight='600'>
                      {invoice.title} {invoice.userfirstname}{' '}
                      {invoice.userlastname}
                    </Typography>
                    <Typography fontSize='0.73rem'>
                      {invoice.userEmail}
                    </Typography>
                    <Typography fontSize='0.73rem'>
                      {invoice.usermobilenumber}
                    </Typography>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  flex: '1 1 48%',
                  minWidth: '280px',
                  p: 1.8,
                  background: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                }}>
                <Typography
                  fontWeight='700'
                  fontSize='0.85rem'
                  mb={0.8}
                  color='#2e7d32'>
                  Guest & Booking Details
                </Typography>
                <Typography fontSize='0.73rem'>
                  <strong>Name:</strong> {invoice.userfirstname}{' '}
                  {invoice.userlastname}
                </Typography>
                <Typography fontSize='0.73rem'>
                  <strong>Nationality:</strong> {invoice.nationality}
                </Typography>
                <Typography fontSize='0.73rem'>
                  <strong>Passport:</strong> {invoice.passportNumber} |{' '}
                  <strong>Visa:</strong> {invoice.visaNumber}
                </Typography>
                <Typography fontSize='0.73rem'>
                  <strong>Email:</strong> {invoice.userEmail}
                </Typography>
                <Typography fontSize='0.73rem'>
                  <strong>Mobile:</strong> {invoice.usermobilenumber}
                </Typography>
                <Divider sx={{ my: 0.8 }} />
                <Typography fontSize='0.73rem'>
                  Adults: {invoice.adultCount} | Children: {invoice.childCount}
                </Typography>
                <Typography fontSize='0.73rem'>
                  Room(s): {invoice.roomNumbers ? String(invoice.roomNumbers).split(',').map((n) => roomLabel(n.trim())).join(', ') : invoice.roomNumbers} | Type: {invoice.roomType}
                </Typography>
                {invoice.packageName && (
                  <Typography fontSize='0.73rem'>
                    <strong>Package:</strong> {invoice.packageName}
                  </Typography>
                )}
                <Typography fontSize='0.73rem'>
                  <strong>Check-In:</strong>{' '}
                  {checkInDate.toLocaleString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </Typography>

                <Typography fontSize='0.73rem'>
                  <strong>Check-Out:</strong>{' '}
                  {checkOutDate.toLocaleString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </Typography>
              </Box>
            </Box>

            {/* ==================== INVOICE TABLE (per-item GST breakdown) ==================== */}
            {(() => {
              // ----- Build the unified line-item list -----
              // Room charge line (first invoice only) and Other Service lines
              // (add-on invoice). Each line carries its own GST% so the table
              // can render the bifurcation table the user asked for:
              //   Name | HSN | Qty | Price | GST% | GST Amount | Total
              const lines = [];

              if (!isAddonsOnlyInvoice) {
                const roomGstPct = Number(invoice.gst || 0);
                const roomBaseTotal = Number(basePrice || 0); // pre-GST room (cover stripped)
                const roomGstAmt =
                  Number(invoice.cGst || 0) +
                  Number(invoice.sGst || 0) +
                  Number(invoice.igst || 0) -
                  coverGstAmt;
                // Show the ORIGINAL room price, then a Discount line, so the
                // bill reads e.g. 1000 − 300 = 700 (+ GST). No discount → the
                // line stays at the actual price (behaviour unchanged) and no
                // discount row is shown. Totals reconcile because the discount
                // is a real (negative) line in the table.
                // A discount line must appear ONLY when a real discount was
                // recorded on the booking. invoice.price is GST-INCLUSIVE while
                // roomnightbasprice is the ex-tax base, so their difference is
                // simply the room GST — showing it as a "Discount" invented a
                // phantom discount on every no-discount booking.
                const hasRealDiscount =
                  Number(invoice.discountValue || 0) > 0 &&
                  (invoice.discountType || '').trim() !== '';
                const origPerNight = Number(invoice.price || 0);
                const roomDiscountTotal = hasRealDiscount
                  ? Math.max(0, origPerNight - roomnightbasprice) * nights * roomCount
                  : 0;
                const origRoomBase = roomBaseTotal + roomDiscountTotal;
                lines.push({
                  name: `Room Charges (${formatRate(origPerNight)} × ${nights} × ${roomCount})`,
                  hsn: invoice.roomHsnCode || '',
                  qty: nights * roomCount,
                  base: origRoomBase,
                  gstPct: roomGstPct,
                  gstAmt: roomGstAmt,
                  total: origRoomBase + roomGstAmt,
                  taxType: 'GST',
                });
                if (roomDiscountTotal > 0.5) {
                  lines.push({
                    name: 'Discount',
                    hsn: '-',
                    qty: '',
                    base: -roomDiscountTotal,
                    gstPct: 0,
                    gstAmt: 0,
                    total: -roomDiscountTotal,
                    taxType: '',
                    isDiscount: true,
                  });
                }
                // Voucher / coupon deduction — shown as a negative line so the
                // bill reads e.g. 1500 − 1500 = 0 and a 100% voucher reconciles
                // to ₹0. Hidden when no voucher was applied.
                if (couponAmount > 0.5) {
                  lines.push({
                    name: invoice.coupen
                      ? `Voucher (${invoice.coupen})`
                      : 'Voucher',
                    hsn: '-',
                    qty: '',
                    base: -couponAmount,
                    gstPct: 0,
                    gstAmt: 0,
                    total: -couponAmount,
                    taxType: '',
                    isDiscount: true,
                  });
                }
                // Extra Room Charges line (base + its GST). CGST/SGST come from
                // the line's gstAmt via lineSplit, so the bifurcation columns
                // fill in automatically. Hidden when there's no room charge.
                if (roomChargeGross > 0) {
                  lines.push({
                    name: 'Room Charges',
                    hsn: '',
                    qty: 1,
                    base: roomChargeBase,
                    gstPct:
                      roomChargeGross > 0
                        ? (roomChargeGstAmt / roomChargeGross) * 100
                        : 0,
                    gstAmt: roomChargeGstAmt,
                    total: roomChargeGross, // gross is GST-inclusive — no add-on
                    taxType: 'GST',
                  });
                }
              }

              if (showAddons && hasAddons) {
                allAddons.forEach((a) =>
                  lines.push({
                    name: a.name,
                    hsn: a.hsn,
                    qty: a.qty || 1,
                    base: a.base,
                    gstPct: a.gstPct,
                    gstAmt: a.gstAmt,
                    total: a.price,
                    taxType: a.taxType || 'GST',
                  }),
                );
              }

              // Restaurant/bar food lines — only on the merged paper.
              if (merged && hasFood) {
                buildFoodLines().forEach((f) => lines.push(f));
              }

              const totalBase = lines.reduce((s, l) => s + l.base, 0);
              const totalGst = lines.reduce((s, l) => s + l.gstAmt, 0);
              const totalAmt = lines.reduce((s, l) => s + l.total, 0);

              // ----- Tax split mode -----
              // Intra-state (CGST+SGST) when the room booking has any
              // CGST/SGST; otherwise inter-state (IGST). VAT lines never
              // split — they get their own column. The per-line breakdown
              // happens inside the inner IIFE below.
              const intraState =
                Number(invoice.cGst || 0) + Number(invoice.sGst || 0) > 0;
              const vatTotal = lines
                .filter((l) => l.taxType === 'VAT')
                .reduce((s, l) => s + l.gstAmt, 0);

              return (
                <Box
                  component='table'
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.78rem',
                    '& th': {
                      background: '#1976d2',
                      color: '#fff',
                      p: '8px 10px',
                      textAlign: 'left',
                      border: '1px solid #1976d2',
                      fontWeight: 600,
                    },
                    '& td': {
                      p: '7px 10px',
                      border: '1px solid #ddd',
                      verticalAlign: 'top',
                    },
                    '@media print': {
                      '& th, & td': { border: '1px solid #000', p: '6px 9px' },
                    },
                  }}>
                  {(() => {
                    // Per-row tax breakdown — VAT items show ONLY in the VAT
                    // column; GST items split into CGST/SGST or IGST based on
                    // the booking's state mode (intra vs inter).
                    // Company GST switch OFF (VAT / No-Tax country) -> NO GST
                    // columns at all. Without gstOn here, !intraState turned the
                    // IGST column ON for non-GST companies and printed an empty
                    // IGST column on a foreign guest's invoice.
                    const showCgstCol = gstOn && intraState;
                    const showSgstCol = gstOn && intraState;
                    const showIgstCol = gstOn && !intraState;
                    const showVatCol  = vatTotal > 0;
                    // Rate % only matters when some tax actually applies.
                    const showRateCol = gstOn || showVatCol;

                    const lineSplit = (l) => {
                      if (l.taxType === 'VAT') {
                        return { cgst: 0, sgst: 0, igst: 0, vat: l.gstAmt };
                      }
                      if (intraState) {
                        return { cgst: l.gstAmt / 2, sgst: l.gstAmt / 2, igst: 0, vat: 0 };
                      }
                      return { cgst: 0, sgst: 0, igst: l.gstAmt, vat: 0 };
                    };

                    const totalSplit = lines.reduce(
                      (acc, l) => {
                        const s = lineSplit(l);
                        return {
                          cgst: acc.cgst + s.cgst,
                          sgst: acc.sgst + s.sgst,
                          igst: acc.igst + s.igst,
                          vat: acc.vat + s.vat,
                        };
                      },
                      { cgst: 0, sgst: 0, igst: 0, vat: 0 },
                    );

                    const totalCols =
                      4 +
                      (showRateCol ? 1 : 0) +
                      (showCgstCol ? 1 : 0) +
                      (showSgstCol ? 1 : 0) +
                      (showIgstCol ? 1 : 0) +
                      (showVatCol ? 1 : 0) +
                      1;

                    return (
                      <>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>HSN</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'right' }}>
                              Price ({Companycurrency})
                            </th>
                            {showRateCol && (
                              <th style={{ textAlign: 'right' }}>Rate %</th>
                            )}
                            {showCgstCol && <th style={{ textAlign: 'right' }}>CGST</th>}
                            {showSgstCol && <th style={{ textAlign: 'right' }}>SGST</th>}
                            {showIgstCol && <th style={{ textAlign: 'right' }}>IGST</th>}
                            {showVatCol  && <th style={{ textAlign: 'right' }}>VAT</th>}
                            <th style={{ textAlign: 'right' }}>
                              Total Price ({Companycurrency})
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {showAddons && hasAddons && (
                            <tr>
                              <td
                                colSpan={totalCols}
                                style={{ background: '#f5faff', fontWeight: 600 }}>
                                Other Services
                              </td>
                            </tr>
                          )}

                          {lines.map((l, i) => {
                            const s = lineSplit(l);
                            return (
                              <tr
                                key={i}
                                style={
                                  l.isDiscount ? { color: '#c62828' } : undefined
                                }>
                                <td>{l.name}</td>
                                <td>{l.hsn || '-'}</td>
                                <td align='right'>{l.qty}</td>
                                <td align='right'>{formatPrice(l.unitPrice != null ? l.unitPrice : l.base)}</td>
                                {showRateCol && (
                                  <td align='right'>
                                    {l.isDiscount ? (
                                      '—'
                                    ) : (
                                      <>
                                        {Number(l.gstPct || 0).toFixed(2)}%
                                        <span style={{ color: '#888', marginLeft: 4 }}>
                                          ({l.taxType})
                                        </span>
                                      </>
                                    )}
                                  </td>
                                )}
                                {showCgstCol && (
                                  <td align='right'>
                                    {s.cgst > 0 ? formatPrice(s.cgst) : '-'}
                                  </td>
                                )}
                                {showSgstCol && (
                                  <td align='right'>
                                    {s.sgst > 0 ? formatPrice(s.sgst) : '-'}
                                  </td>
                                )}
                                {showIgstCol && (
                                  <td align='right'>
                                    {s.igst > 0 ? formatPrice(s.igst) : '-'}
                                  </td>
                                )}
                                {showVatCol && (
                                  <td align='right'>
                                    {s.vat > 0 ? formatPrice(s.vat) : '-'}
                                  </td>
                                )}
                                <td align='right'>{formatPrice(l.total)}</td>
                              </tr>
                            );
                          })}

                          {/* Sub-total row */}
                          <tr>
                            <td colSpan={3} style={{ fontWeight: 600 }}>
                              Sub Total
                            </td>
                            <td align='right'>
                              <strong>{formatPrice(totalBase)}</strong>
                            </td>
                            {showRateCol && <td />}
                            {showCgstCol && (
                              <td align='right'>
                                <strong>{formatPrice(totalSplit.cgst)}</strong>
                              </td>
                            )}
                            {showSgstCol && (
                              <td align='right'>
                                <strong>{formatPrice(totalSplit.sgst)}</strong>
                              </td>
                            )}
                            {showIgstCol && (
                              <td align='right'>
                                <strong>{formatPrice(totalSplit.igst)}</strong>
                              </td>
                            )}
                            {showVatCol && (
                              <td align='right'>
                                <strong>{formatPrice(totalSplit.vat)}</strong>
                              </td>
                            )}
                            <td align='right'>
                              <strong>{formatPrice(totalAmt)}</strong>
                            </td>
                          </tr>

                          {/* Grand Total row */}
                          <tr
                            style={{
                              background: '#1976d2',
                              color: '#fff',
                              fontSize: '1rem',
                              fontWeight: 700,
                            }}>
                            <td colSpan={totalCols - 1}>Grand Total</td>
                            <td align='right'>{formatPrice(grandTotal)}</td>
                          </tr>
                        </tbody>
                      </>
                    );
                  })()}
                </Box>
              );
            })()}

            <Box textAlign='right' mt={2} mb={2}>
              <Typography fontSize='0.8rem' fontStyle='italic' fontWeight='500'>
                <strong>In Words:</strong> {grandTotalInWords}
              </Typography>
            </Box>

            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={2.5}>
              <Typography fontSize='0.60rem'>
                <strong>Bank Name:</strong> {company.bankName}
                <br />
                <strong>Account No:</strong> {company.bankAccountNumber}
                <br />
                <strong>IFSC:</strong> {company.ifsc}
                <br />
                <strong>Bank Address:</strong> {company.bankAddress}
              </Typography>
            </Box>

            {termsAndConditions && (
              <Box mt={3} mb={3}>
                <Typography
                  variant='subtitle1'
                  fontWeight='700'
                  color='#c62828'
                  gutterBottom>
                  Terms & Conditions
                </Typography>
                <Typography
                  fontSize='0.73rem'
                  sx={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                  {termsAndConditions}
                </Typography>
              </Box>
            )}

            <Box
              mt={4}
              display='flex'
              justifyContent='space-between'
              flexWrap='wrap'
              gap={4}>
              <Box textAlign='center' flex={1}>
                <Box height='65px' borderBottom='2px solid #333' mb={1} />
                <Typography fontWeight='600'>Customer Signature</Typography>
              </Box>
              <Box textAlign='center' flex={1}>
                <Box height='65px' borderBottom='2px solid #333' mb={1} />
                <Typography fontWeight='600'>Authorized Signature</Typography>
                <Typography variant='caption'>
                  For {invoice.hotelName}
                </Typography>
              </Box>
            </Box>

            <Box
              textAlign='center'
              mt={3}
              fontSize='0.72rem'
              color='text.secondary'>
              This is a computer generated invoice and does not require physical
              signature.
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
